import { useEffect, useState } from "react";
import { Hourglass, Mail, CheckCircle2, Clock } from "lucide-react";
import api from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const STEP_LABELS = {
  pricing_calculator: "Pricing calculator",
  quote_view: "Quote viewed",
  scheduler_open: "Scheduler opened",
  booking_started: "Booking started",
};

const statusOf = (r) => {
  if (r.follow_up_sent && r.completed) return ["converted", "bg-sage/15 text-sage"];
  if (r.completed) return ["completed", "bg-sage/15 text-sage"];
  if (r.follow_up_sent) return ["follow-up sent", "bg-terracotta-light text-terracotta-700"];
  return ["pending", "bg-sand-200 text-charcoal-600"];
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export default function FollowUpsManager() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/abandoned-sessions").then((r) => setData(r.data)).catch(() => setData({ recent: [] })); }, []);
  if (!data) return <p className="text-charcoal-500">Loading…</p>;

  const stats = [
    { label: "Visits tracked", value: data.total || 0, icon: Hourglass, testid: "fu-stat-total" },
    { label: "Follow-ups sent", value: data.followups_sent || 0, icon: Mail, testid: "fu-stat-sent" },
    { label: "Converted after follow-up", value: data.converted || 0, icon: CheckCircle2, testid: "fu-stat-converted" },
    { label: "Waiting (no email yet or <45 min)", value: data.pending || 0, icon: Clock, testid: "fu-stat-pending" },
  ];

  return (
    <div data-testid="admin-followups">
      <SectionTitle title="Abandoned visits & follow-ups"
        subtitle="Visitors who started something but didn't finish. Each gets exactly one warm, no-rush email after 45 minutes — never more." />
      <div className="mb-6 grid gap-3 sm:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label}>
            <div className="flex items-center gap-3" data-testid={s.testid}>
              <s.icon size={20} className="text-terracotta shrink-0" strokeWidth={1.5} />
              <div>
                <div className="font-serif text-2xl text-charcoal-800">{s.value}</div>
                <div className="text-xs text-charcoal-500">{s.label}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {data.by_step && Object.keys(data.by_step).length > 0 && (
        <Card className="mb-6">
          <h3 className="font-medium text-charcoal-800 mb-3">By step</h3>
          <div className="grid gap-2 sm:grid-cols-4">
            {Object.entries(data.by_step).map(([step, s]) => (
              <div key={step} className="rounded-xl bg-sand-100 px-4 py-3" data-testid={`fu-step-${step}`}>
                <div className="text-sm font-medium text-charcoal-800">{STEP_LABELS[step] || step}</div>
                <div className="text-xs text-charcoal-500 mt-1">{s.count} tracked · {s.sent} emailed · {s.converted} converted</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <h3 className="font-medium text-charcoal-800 mb-3">Recent activity</h3>
        {(data.recent || []).length === 0 ? (
          <p className="text-sm text-charcoal-500">Nothing tracked yet — records appear when visitors use the calculator, view a quote, open the scheduler, or start a booking.</p>
        ) : (
          <div className="divide-y divide-sand-100">
            {data.recent.map((r) => {
              const [label, style] = statusOf(r);
              return (
                <div key={r.id} data-testid={`fu-row-${r.id}`} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-charcoal-800">{STEP_LABELS[r.step] || r.step}</div>
                    <div className="truncate text-xs text-charcoal-500">
                      {r.name ? `${r.name} · ` : ""}{r.email || "email not captured yet"} · last seen {fmt(r.last_seen)}
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${style}`}>{label}</span>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-xs text-charcoal-400">Failed sends appear under the failed-emails monitor. Follow-ups respect completions and unsubscribes automatically.</p>
      </Card>
    </div>
  );
}
